/**
 * Minimal HTML tagged-template helper.
 *
 * Handles interpolation in:
 *  - element children (text + Node + Array<Node> + Raw)
 *  - attribute values (string + number; rendered as text — Node values inside
 *    attributes are not meaningful)
 *
 * Strings inserted as children are escaped as text UNLESS they start with `<`
 * (a narrow whitelist for inline SVG icons, see icons.ts). Wrap explicit raw
 * HTML in `raw()` to be safe.
 *
 * Bundle weight: ~900 bytes minified.
 */

export class Raw {
  constructor(public html: string) {}
}
export const raw = (s: string): Raw => new Raw(s);

export type Renderable =
  | string
  | number
  | Node
  | Node[]
  | DocumentFragment
  | Raw
  | boolean
  | null
  | undefined;

// Use an unusual sentinel that survives HTML-attribute and text parsing
// without being interpreted as markup.
const SLOT = (i: number): string => `__$FUDETO_${i}__`;
const SLOT_RE = /__\$FUDETO_(\d+)__/g;

export function html(strings: TemplateStringsArray, ...vals: Renderable[]): DocumentFragment {
  const src = strings.reduce((acc, s, i) => acc + s + (i < vals.length ? SLOT(i) : ''), '');
  const tpl = document.createElement('template');
  tpl.innerHTML = src;
  const frag = tpl.content;

  // Walk all elements + text nodes; replace any placeholders found in
  // attribute values or text content.
  const toReplaceText: Array<{ node: Text }> = [];
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let cur: Node | null = walker.nextNode();
  while (cur) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      const eEl = cur as Element;
      for (let i = 0; i < eEl.attributes.length; i++) {
        const a = eEl.attributes[i]!;
        if (a.value.includes('__$FUDETO_')) {
          a.value = a.value.replace(SLOT_RE, (_, n) => {
            const v = vals[Number(n)];
            return v == null || v === false || v === true ? '' : String(v);
          });
        }
      }
    } else if (cur.nodeType === Node.TEXT_NODE) {
      const t = cur as Text;
      if (t.data.includes('__$FUDETO_')) toReplaceText.push({ node: t });
    }
    cur = walker.nextNode();
  }

  for (const { node } of toReplaceText) {
    const parent = node.parentNode;
    if (!parent) continue;
    const segments = splitText(node.data);
    const newNodes: Node[] = [];
    for (const seg of segments) {
      if (typeof seg === 'string') {
        if (seg) newNodes.push(document.createTextNode(seg));
        continue;
      }
      const v = vals[seg];
      if (v == null || v === false || v === true) continue;
      if (v instanceof Raw) {
        const tpl2 = document.createElement('template');
        tpl2.innerHTML = v.html;
        for (const child of Array.from(tpl2.content.childNodes)) newNodes.push(child);
        continue;
      }
      if (v instanceof Node) {
        newNodes.push(v);
        continue;
      }
      if (Array.isArray(v)) {
        for (const n of v) if (n) newNodes.push(n);
        continue;
      }
      const s = String(v);
      // Heuristic: strings starting with '<' are inline SVG / HTML markup.
      if (s.length > 1 && s.charCodeAt(0) === 60) {
        const tpl2 = document.createElement('template');
        tpl2.innerHTML = s;
        for (const child of Array.from(tpl2.content.childNodes)) newNodes.push(child);
      } else {
        newNodes.push(document.createTextNode(s));
      }
    }
    for (const n of newNodes) parent.insertBefore(n, node);
    parent.removeChild(node);
  }

  return frag;
}

/** Split a string by SLOT placeholders, returning interleaved string|number segments. */
function splitText(s: string): Array<string | number> {
  const out: Array<string | number> = [];
  let last = 0;
  SLOT_RE.lastIndex = 0;
  let m: RegExpExecArray | null = SLOT_RE.exec(s);
  while (m) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(Number(m[1]));
    last = m.index + m[0].length;
    m = SLOT_RE.exec(s);
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

/** Build an HTMLElement from a single-root html`` fragment (throws otherwise). */
export function el(frag: DocumentFragment): HTMLElement {
  const first = frag.firstElementChild;
  if (!first) throw new Error('html: empty fragment');
  if (first.nextElementSibling) throw new Error('html: multiple roots');
  return first as HTMLElement;
}

/** Bind delegated click handler with optional data-attribute filter. */
export function on<K extends keyof HTMLElementEventMap>(
  root: HTMLElement,
  event: K,
  selector: string,
  handler: (target: HTMLElement, ev: HTMLElementEventMap[K]) => void,
): void {
  root.addEventListener(event, (ev) => {
    const t = (ev.target as HTMLElement | null)?.closest(selector);
    if (t && root.contains(t)) handler(t as HTMLElement, ev as HTMLElementEventMap[K]);
  });
}
